import { DatePicker, Form, Input, Radio } from "antd"
import { type FormInstance } from "antd/lib/form"
import { type Store } from "antd/lib/form/interface"
import moment from "moment"
import React from "react"
import { Search as SearchIcon } from "react-feather"
import { useTranslation } from "react-i18next"
import { createArchiveSearchRules } from "~Components/forms/rules/archiveSearch.rules"
import { TransactionTabTypeEnum } from "~Features/pages/transactions/TransactionsList"
import { useAuth } from "~Shared/hooks/auth.hooks"
import { TablesEnum } from "~Shared/store/reducers/tableConfigReducer"
import { ExportDateFilterEnum } from "~Shared/utils/exportDateFilter.enum"

const { RangePicker } = DatePicker

interface ArchiveModalFormProps {
    formInstance: FormInstance
    onSubmit: (values: Store) => void
    archiveTable: TablesEnum.ARCHIVE | TablesEnum.TRANSACTIONS_ARCHIVE
}

export const ArchiveModalForm = ({ formInstance, onSubmit, archiveTable }: ArchiveModalFormProps) => {
    const { t } = useTranslation()
    const { isFinwayAdmin } = useAuth()
    const rules = createArchiveSearchRules({ isFinwayAdmin })
    const archiveDateFilter = Form.useWatch("archiveDateFilter", formInstance) ?? ExportDateFilterEnum.PERFORMANCE
    const isExpenseArchive = archiveTable === TablesEnum.ARCHIVE
    const isInvoiceDateFilter = isExpenseArchive && archiveDateFilter === ExportDateFilterEnum.INVOICE
    const periodLabel = isExpenseArchive
        ? t(isInvoiceDateFilter ? "input:expense_filter.invoice_date_range" : "input:expense_filter.purchase_date_range")
        : t("input:archive.searchPeriod")

    return (
        <Form id="expenseSearchForm" layout="vertical" form={formInstance} onFinish={onSubmit}>
            {archiveTable === TablesEnum.TRANSACTIONS_ARCHIVE && (
                <Form.Item className="gap-0 w-full" name="transactionTabType" initialValue={TransactionTabTypeEnum.ALL}>
                    <Radio.Group className="flex flex-wrap">
                        <Radio.Button className="flex-grow text-xs sm:text-base text-center" value={TransactionTabTypeEnum.ALL}>
                            {t("label:all")}
                        </Radio.Button>
                        <Radio.Button className="flex-grow text-xs sm:text-base text-center" value={TransactionTabTypeEnum.OUTFLOW}>
                            {t("label:outflow")}
                        </Radio.Button>
                        <Radio.Button className="flex-grow text-xs sm:text-base text-center" value={TransactionTabTypeEnum.INFLOW}>
                            {t("label:inflow")}
                        </Radio.Button>
                    </Radio.Group>
                </Form.Item>
            )}

            <Form.Item name="searchString" extra={t("info:archive.expense_initial_display.warning")}>
                <Input className="w-full" size="small" placeholder={t("placeholder:search")} prefix={<SearchIcon size={15} />} allowClear />
            </Form.Item>

            {isExpenseArchive && (
                <Form.Item label={t("input:export.date_filter")} name="archiveDateFilter" initialValue={ExportDateFilterEnum.PERFORMANCE}>
                    <Radio.Group>
                        <Radio className="mr-24" value={ExportDateFilterEnum.PERFORMANCE}>
                            {t("label:performance_date")}
                        </Radio>
                        <Radio value={ExportDateFilterEnum.INVOICE}>{t("label:invoice_date")}</Radio>
                    </Radio.Group>
                </Form.Item>
            )}

            <Form.Item
                label={periodLabel}
                name="period"
                required
                rules={rules.period}
                extra={isExpenseArchive ? undefined : t("info:archive.transaction_search_info")}
                initialValue={[moment().subtract(24, "months"), moment()]}
            >
                <RangePicker className="w-full" format={moment.localeData().longDateFormat("L")} />
            </Form.Item>
        </Form>
    )
}
